import React, { useState, useEffect, useCallback } from 'react';
import Pusher from 'pusher-js';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Button,
  Tabs,
  Tab,
  Badge,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Person,
  Message,
  Settings,
  Notifications as NotificationIcon,
  MoreVert,
  MarkEmailRead,
  Delete,
  FilterList,
  CheckCircle
} from '@mui/icons-material';
import apiEndpoints from '../../apiconfig';

const Notifications = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetNotification, setDeleteTargetNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  // API endpoint configuration
  // const API_BASE_URL = apiEndpoints.notification;
  const API_BASE_URL = apiEndpoints.notification;


  // Function to get auth token (adjust based on your auth system)
  const getAuthToken = () => {
    return sessionStorage.getItem('token') || '';
  };

  // Function to format time for display
  const formatTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  // Map API data to UI format
  const mapApiDataToUiFormat = (apiData) => {
    return apiData.map(notification => {
      // Determine notification type based on your API data
      let type = 'system';
      if (notification.message && notification.message.toLowerCase().includes('applied')) {
        type = 'application';
      } else if (notification.message && notification.message.toLowerCase().includes('message')) {
        type = 'message';
      }

      // Determine color based on type
      let color = '#7c3aed'; // default system color
      if (type === 'application') color = '#2563eb';
      if (type === 'message') color = '#059669';

      return {
        id: notification.id,
        dbId: notification.id, // Store the actual DB ID for API operations
        type: type,
        title: notification.sender_name.split(':')[0] || 'Notification',
        message: notification.message,
        time: formatTime(notification.createdOn),
        isRead: Boolean(notification.isRead),
        avatar: notification.sender_image || null,
        color: color
      };
    });
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}?action=get`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        const formattedNotifications = mapApiDataToUiFormat(data.data);
        setNotifications(formattedNotifications);
      } else {
        setError(data.message || 'Failed to fetch notifications');
        // Fallback to sample data if API fails
        setNotifications([
          {
            id: 1,
            type: 'application',
            title: 'New Application Received',
            message: 'Sarah Johnson applied for Senior React Developer position',
            time: '2 minutes ago',
            isRead: false,
            avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150',
            color: '#2563eb'
          },
          // ... include other sample data as fallback
        ]);
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Fetch error:', err);
      // Fallback to sample data
      setNotifications([
        {
          id: 1,
          type: 'application',
          title: 'New Application Received',
          message: 'Sarah Johnson applied for Senior React Developer position',
          time: '2 minutes ago',
          isRead: false,
          avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150',
          color: '#2563eb'
        },
        {
          id: 2,
          type: 'message',
          title: 'New Message',
          message: 'Mike Chen sent you a message about the Full Stack Developer role',
          time: '15 minutes ago',
          isRead: false,
          avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150',
          color: '#059669'
        },
        {
          id: 3,
          type: 'application',
          title: 'Application Withdrawn',
          message: 'John Smith withdrew his application for UI/UX Designer position',
          time: '1 hour ago',
          isRead: true,
          avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150',
          color: '#dc2626'
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;

    let receiver_guid = null;
    try {
      const tokenPayload = token.split('.')[1];
      if (!tokenPayload) return;
      const decoded = JSON.parse(atob(tokenPayload));
      receiver_guid = decoded?.userGuid;
      if (!receiver_guid) return;
    } catch (decodeError) {
      console.error('Invalid auth token:', decodeError);
      return;
    }

    const pusher = new Pusher('ea8941fae9b57f3fbff9', {
      cluster: 'ap2',
      forceTLS: true,
    });

    const channel = pusher.subscribe(`user-${receiver_guid}`);

    channel.bind('new-notification', function () {
      fetchNotifications();
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
      pusher.disconnect();
    };
  }, [fetchNotifications]);

  const readNotifications = notifications.filter(n => n.isRead);
  const unreadNotifications = notifications.filter(n => !n.isRead);

  const categories = [
    { label: 'All', count: notifications.length },
    { label: 'Read', count: readNotifications.length },
    { label: 'Unread', count: unreadNotifications.length },
    // { label: 'Applications', count: notifications.filter(n => n.type === 'application').length },
    // { label: 'Messages', count: notifications.filter(n => n.type === 'message').length },
    // { label: 'System', count: notifications.filter(n => n.type === 'system').length }
  ];

  const getFilteredNotifications = () => {
    if (activeTab === 0) return [...unreadNotifications, ...readNotifications]; // All
    if (activeTab === 1) return readNotifications; // Read
    if (activeTab === 2) return unreadNotifications;

    const typeMap = {
      1: 'application',
      2: 'message',
      3: 'system'
    };

    return notifications.filter(notification =>
      notification.type === typeMap[activeTab]
    );
  };

  const getIcon = (type) => {
    switch (type) {
      case 'application': return <Person />;
      case 'message': return <Message />;
      case 'system': return <Settings />;
      default: return <NotificationIcon />;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event, notification) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuAnchorPosition({
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.right),
    });
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setMenuAnchorPosition(null);
    setSelectedNotification(null);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const markAsRead = async (id) => {
    try {
      // Update UI first for immediate feedback
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, isRead: true } : notification
        )
      );

      // Then call API
      const response = await fetch(`${API_BASE_URL}?action=updateisread`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: selectedNotification.dbId }),
      });

      const data = await response.json();

      if (!data.success) {
        // Revert UI change if API call failed
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === id ? { ...notification, isRead: false } : notification
          )
        );
        showSnackbar('Failed to mark as read', 'error');
      } else {
        showSnackbar(`Marked "${selectedNotification.title}" as read`, 'success');
      }
    } catch (error) {
      // Revert UI change if API call failed
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, isRead: false } : notification
        )
      );
      showSnackbar('Failed to mark as read', 'error');
    }

    handleMenuClose();
  };

  const deleteNotification = async (id) => {
    const actionNotification = deleteTargetNotification || selectedNotification;
    if (!actionNotification) {
      showSnackbar('Failed to delete notification', 'error');
      return;
    }

    try {
      // Store the notification for potential undo
      const notificationToDelete = notifications.find(n => n.id === id);

      // Update UI first for immediate feedback
      setNotifications(prev => prev.filter(notification => notification.id !== id));

      // Then call API
      const response = await fetch(`${API_BASE_URL}?action=delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: actionNotification.dbId }),
      });

      const data = await response.json();

      if (!data.success) {
        // Revert UI change if API call failed
        setNotifications(prev => [...prev, notificationToDelete]);
        showSnackbar('Failed to delete notification', 'error');
      } else {
        showSnackbar(`Deleted "${actionNotification.title}"`, 'info');
      }
    } catch (error) {
      // Revert UI change if API call failed
      const notificationToDelete = notifications.find(n => n.id === id);
      setNotifications(prev => [...prev, notificationToDelete]);
      showSnackbar('Failed to delete notification', 'error');
    }

    setDeleteConfirmOpen(false);
    setDeleteTargetNotification(null);
    handleMenuClose();
  };

  const handleDeleteMenuClick = () => {
    if (!selectedNotification) return;
    setDeleteTargetNotification(selectedNotification);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setDeleteTargetNotification(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTargetNotification) return;
    deleteNotification(deleteTargetNotification.id);
  };

  const markAllAsRead = async () => {
    try {
      // Update UI first for immediate feedback
      setNotifications(prev => prev.map(notification => ({ ...notification, isRead: true })));

      // Then call API
      const response = await fetch(`${API_BASE_URL}?action=updateisread`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!data.success) {
        // Revert UI change if API call failed
        setNotifications(prev => prev.map(notification => ({ ...notification, isRead: false })));
        showSnackbar('Failed to mark all as read', 'error');
      } else {
        showSnackbar('All notifications marked as read', 'success');
      }
    } catch (error) {
      // Revert UI change if API call failed
      setNotifications(prev => prev.map(notification => ({ ...notification, isRead: false })));
      showSnackbar('Failed to mark all as read', 'error');
    }
  };

  const NotificationItem = ({ notification }) => (
    <ListItem
      sx={{
        my: { xs: "6px", sm: "10px" },
        mx: 0,
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        borderRadius: { xs: "14px", sm: "16px" },
        p: { xs: "10px 12px", sm: "14px 16px", md: "16px 18px" },
        border: "1px solid #dfe9e8",
        background: notification.isRead
          ? "linear-gradient(to right, #f8fbfb, #ffffff)"
          : "linear-gradient(to right, #eaf6f5, #ffffff)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
        alignItems: { xs: "flex-start", sm: "center" },
        overflow: "hidden",
        '&:hover': {
          transform: { xs: "none", sm: "translateY(-3px)" },
          boxShadow: { xs: "0 4px 12px rgba(0,0,0,0.05)", sm: "0 10px 22px rgba(31,63,62,0.12)" }
        }
      }}
    >
      <ListItemAvatar sx={{ minWidth: { xs: 44, sm: 56 }, mt: { xs: 0.5, sm: 0 } }}>
        {notification.avatar ? (
          <Avatar
            src={notification.avatar}
            alt="User"
            sx={{
              width: { xs: 36, sm: 44, md: 50 },
              height: { xs: 36, sm: 44, md: 50 },
              border: "2px solid #d9ecea",
              boxShadow: "0 4px 10px rgba(47,111,109,0.18)"
            }}
          />
        ) : (
          <Avatar
            sx={{
              background: "linear-gradient(135deg, #67b7a8 0%, #2f6f6d 100%)",
              color: 'white',
              width: { xs: 36, sm: 44, md: 50 },
              height: { xs: 36, sm: 44, md: 50 },
              boxShadow: "0 4px 10px rgba(47,111,109,0.22)"
            }}
          >
            {getIcon(notification.type)}
          </Avatar>
        )}
      </ListItemAvatar>

      <ListItemText
        sx={{ minWidth: 0, m: 0 }}
        primary={
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: "flex-start", sm: "flex-start" },
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              minWidth: 0
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: '#1f3f3e',
                fontSize: { xs: '0.9rem', sm: '0.98rem' },
                lineHeight: 1.4,
                flex: 1,
                minWidth: 0,
                pr: 1
              }}
            >
              {notification.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: "auto" }}>
              {!notification.isRead && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: notification.color,
                    flexShrink: 0
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, notification)}
                sx={{
                  padding: '4px',
                  color: '#8a9b9a',
                  '&:hover': {
                    color: '#2f6f6d',
                    backgroundColor: 'rgba(47,111,109,0.08)'
                  }
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#5f6f6e',
                marginBottom: '8px',
                lineHeight: 1.45,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "normal",
                display: '-webkit-box',
                WebkitLineClamp: { xs: 3, sm: 2 },
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {notification.message}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: 'space-between',
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0.75, sm: 1 },
                minWidth: 0
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#9aabaa',
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  fontWeight: 400
                }}
              >
                {notification.time}
              </Typography>
              <Chip
                label={notification.type}
                size="small"
                sx={{
                  backgroundColor: `${notification.color}15`,
                  color: notification.color,
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                  height: { xs: 18, sm: 20 },
                  textTransform: 'capitalize',
                  fontWeight: 500,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  '& .MuiChip-label': {
                    padding: { xs: '0 6px', sm: '0 8px' }
                  }
                }}
              />
            </Box>
          </Box>
        }
      />
    </ListItem>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1200px",
        mx: "auto",
        background:
          "radial-gradient(circle at 0% 0%, rgba(219, 234, 254, 0.6) 0%, rgba(248, 250, 252, 0) 42%), radial-gradient(circle at 100% 0%, rgba(233, 213, 255, 0.45) 0%, rgba(248, 250, 252, 0) 40%)",
        overflowX: "hidden",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        py: { xs: 1, sm: 3 },
        px: { xs: 1, sm: 3 },
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'block',
          marginBottom: 3,
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: { xs: "16px", sm: "20px" },
            border: "1px solid #E2E8F0",
            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: 1,
                fontSize: { xs: '1.65rem', sm: '2rem', md: '2.1rem' }
              }}
            >
              Notifications
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Stay updated with your job postings and applications
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={markAllAsRead}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#36656B',
              boxShadow: '0 10px 22px rgba(54,101,107,0.28)',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              padding: { xs: '8px 14px', sm: '10px 18px' },
              width: { xs: "100%", sm: "auto" },
              minHeight: { xs: 42, sm: 44 },
              '&:hover': {
                backgroundColor: '#2B4F54',
                boxShadow: '0 12px 24px rgba(43,79,84,0.34)'
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Mark All Read
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Mark All
            </Box>
          </Button>
          </Box>
        </Box>
      </Box>

      {/* Notification Card */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: "16px", sm: "20px" },
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Tabs */}
          <Box
            sx={{
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f9fafb',
              flexShrink: 0
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                padding: { xs: '0 8px', sm: '0 16px' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: { xs: 44, sm: 48 },
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  color: '#6b7280',
                  '&.Mui-selected': {
                    color: '#2f6f6d'
                  }
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              {categories.map((category, index) => (
                <Tab
                  key={category.label}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category.label}
                      <Badge
                        badgeContent={category.count}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: activeTab === index ? '#dff3f1' : '#f3f4f6',
                            color: activeTab === index ? '#2f6f6d' : '#6b7280',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            height: { xs: 16, sm: 18 },
                            minWidth: { xs: 16, sm: 18 },
                            fontWeight: 700
                          }
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Notifications List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : (
            <List sx={{ px: { xs: 1, sm: 1.75 }, py: { xs: 1, sm: 1.5 }, flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', background: "linear-gradient(180deg, rgba(248,250,252,0.45) 0%, rgba(255,255,255,0.7) 100%)" }}>
              {getFilteredNotifications().length > 0 ? (
                getFilteredNotifications().map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: { xs: '40px 20px', sm: '60px 20px' },
                    textAlign: 'center',
                    flexGrow: 1,
                    margin: { xs: "6px 0", sm: "8px 0" },
                    borderRadius: "16px",
                    border: "1px solid #e6edf7",
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
                  }}
                >
                  <NotificationIcon
                    sx={{
                      fontSize: { xs: 48, sm: 64 },
                      color: '#d1d5db',
                      marginBottom: 2
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#6b7280',
                      marginBottom: 1,
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      fontWeight: 600
                    }}
                  >
                    No notifications
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9ca3af',
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    You're all caught up! Check back later for updates.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Paper>
      </Box>

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuAnchorPosition || undefined}
        open={Boolean(menuAnchorPosition)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 160,
            boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
          }
        }}
      >
        {selectedNotification && !selectedNotification.isRead && (
          <MenuItem onClick={() => markAsRead(selectedNotification.id)}>
            <MarkEmailRead sx={{ mr: 1, fontSize: 18, color: '#059669' }} />
            Mark as Read
          </MenuItem>
        )}

        {selectedNotification && (
          <MenuItem
            onClick={handleDeleteMenuClick}
            sx={{ color: '#dc2626' }}
          >
            <Delete sx={{ mr: 1, fontSize: 18 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Notification</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this notification?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteConfirmClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
